import PropTypes from 'prop-types';
import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {findDOMNode} from 'react-dom';
import {ButtonIcon} from '@homeaway/react-buttons';
import {CLOSE__16} from '@homeaway/svg-defs';
import {TrapFocus} from '@homeaway/a11y-tools';
import classNames from 'classnames';
import TooltipPositioner from './TooltipPositioner';
import {checkElementType} from './utils';
import {addThrottleHandler, resize, scroll} from '@homeaway/raf-optimized';
import './Tooltip.less';

class Tooltip extends Component {
    static propTypes = {
        /** Delay closing the tooltip in milliseconds */
        delayHide: PropTypes.number,
        /** Delay opening the tooltip in milliseconds */
        delayShow: PropTypes.number,
        /** Whether the tooltip should fade out or removed from the DOM instantly */
        fadeOut: PropTypes.bool,
        /** Content of the Tooltip */
        content: PropTypes.node.isRequired,
        /** If using your own state to control the visibility of the Tooltip, rather than providing an
            openByClickOn element or children, you will set the isOpen property. Also set the trigger to "none".  */
        isOpen: PropTypes.bool,
        /** The function to call when the tooltip closes */
        onClose: PropTypes.func,
        /** Callback for when the tooltip gains focus */
        onFocus: PropTypes.func,
        /** The function to call when the tooltip opens */
        onOpen: PropTypes.func,
        /** The element to attach an event listener that opens the Tooltip */
        openByClickOn: PropTypes.element,
        /** How to position the tooltip */
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        /** Whether to center the tooltip to its content or allow for the default offset */
        centered: PropTypes.bool,
        /** The DOM element to use as the parent for the tooltip when visible. This is useful in scenarios where the tooltip needs to break out of its current container due to overflow or z-index issues. */
        parentNode: checkElementType,
        /** Allows you to add a classname to the popover/tooltip for custom styling */
        portalClassName: PropTypes.string,
        /** The DOM element that the tooltip needs to scroll with. Only specify this if parentNode was specified and the tooltip is displayed in a container that is scrollable independent of the browser window. */
        scrollNode: checkElementType,
        /** Tooltip Title, used only in tooltipType === 'popover' */
        title: PropTypes.node,
        /** Type of Tooltip (Tooltip, Popover, Large Tooltip) to display */
        tooltipType: PropTypes.oneOf(['tooltip', 'popover', 'tooltip--lg']),
        /** How tooltip is triggered: click | hover | focus | none. You may pass multiple triggers; separate them with a space. Set this to "none" if you are controlling the tooltip/popover visibility externally using the "isOpen" property. */
        trigger: PropTypes.string,
        /** className prop to pass to the wrapper div. Defaults to an empty string. */
        wrapperClassName: PropTypes.string,
        /** style prop to pass to the wrapper div. Defaults to an empty object. */
        wrapperStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** If the element that you are applying the tooltip to is a block element, switch the wrappingMethod from its default to 'block'.
         * This prop is left for compatibility with older versions and the display value may be overridden with `wrapperStyle` instead. */
        wrappingMethod: PropTypes.oneOf(['inline-block', 'block']),
        /** Boolean to determine whether the tooltip will close if the user clicks outside of the tooltip. */
        closeOnOutsideClick: PropTypes.bool,
        /** Flag to use when Tooltip content contains elements that are interactive. This will remove the default `aria-live` attribute and `tabindex`. As a result, it will prevent aria-live from causing a screen reader to read out all elements within a Tooltip (ex: a calendar) when it opens. Defaults to false.*/
        interactiveContent: PropTypes.bool,
        /** Flag to use when a Tooltip is used on an element that can receive its own focus. This will remove the `role="button"` attribute and `tabindex` from the span wrapper. Ie: When using a Button, FormInput, etc. */
        interactiveContainer: PropTypes.bool,
        /** Flag to use when a tooltip should match the width of the element that it is attached to. Defaults to false. */
        fullWidth: PropTypes.bool,
        /** Force using window bounds (instead of body) for popovers. Defaults to false. */
        useClientHeight: PropTypes.bool,
        /** Should persist until closed. Implies close button is visible. */
        shouldPersist: PropTypes.bool,
        /** Localized labels */
        labels: PropTypes.shape({
            close: PropTypes.string
        })
    };

    static defaultProps = {
        content: '',
        delayHide: 0,
        delayShow: 0,
        fadeOut: true,
        isOpen: false,
        onClose: () => {},
        onFocus: () => {},
        onOpen: () => {},
        placement: 'top',
        centered: false,
        tooltipType: 'tooltip',
        trigger: 'hover focus',
        wrapperClassName: '',
        wrapperStyle: {},
        wrappingMethod: 'inline-block',
        closeOnOutsideClick: true,
        interactiveContent: false,
        interactiveContainer: false,
        fullWidth: false,
        useClientHeight: false,
        hasCloseButton: false,
        labels: {
            close: 'Close'
        }
    };

    constructor(props) {
        super(props);

        this.closeTimeoutId = null;
        this.delayHideTimeoutId = null;
        this.delayShowTimeoutId = null;
        this.removeResizeEventListener = Function.prototype; // Noop
        this.contentId = null;
        this.trapFocus = new TrapFocus();

        this.state = {
            persist: this.props.shouldPersist,
            displayTooltip: false
        };

        this.KEYCODES = {
            ESCAPE: 27
        };
    }

    componentDidMount() {
        this.contentId = Math.floor(Math.random() * 1000);

        if (this.props.isOpen) {
            this.displayTooltip();
        }

        this.addEventListeners();
    }

    componentDidUpdate(prevProps, prevState) {
        const {isOpen, parentNode, scrollNode} = this.props;
        const {displayTooltip} = this.state;

        if (!!prevProps.isOpen !== !!isOpen) {
            if (isOpen) {
                this.displayTooltip();
            } else {
                this.fadeAndClose();
            }
        }

        this.queryFocusableElements();

        const displayChanged = prevState.displayTooltip !== displayTooltip;
        this.adjustPositioning();

        // If the tooltip needs to scroll independent of the browser window,
        // listen to scroll events on the specified scrollNode and the browser
        // window to adjust positioning appropriately.
        if (
            displayChanged &&
            typeof window !== 'undefined' &&
            displayTooltip &&
            parentNode &&
            scrollNode
        ) {
            // Setup listeners when tooltip is displayed.
            this.removeNodeScrollListener = addThrottleHandler(
                'scroll',
                this.adjustPositioning,
                scrollNode
            );
            this.removeWindowScrollListener = scroll(this.adjustPositioning);
        }

        if (displayChanged && !displayTooltip) {
            // Remove listeners when tooltip is hidden.
            if (this.removeNodeScrollListener) {
                this.removeNodeScrollListener();
                this.removeNodeScrollListener = null;
            }
            if (this.removeWindowScrollListener) {
                this.removeWindowScrollListener();
                this.removeWindowScrollListener = null;
            }
        }
    }

    componentWillUnmount() {
        this.clearTimeouts();
        this.detachEventListeners();
    }

    // Queries for all focusable elements within the popover container
    queryFocusableElements = () => {
        const targetContainer = this.tooltip;
        const tooltipType = this.props.tooltipType;

        if (targetContainer && tooltipType === 'popover') {
            const node = findDOMNode(targetContainer);
            this.trapFocus.determineFocusable(node);
        }
    };

    // Resets the focused element to the first focusable element within the container
    resetFocus = () => {
        this.queryFocusableElements();
        this.trapFocus.resetFocus();
    };

    addEventListeners = () => {
        // @homeaway/raf-optimized resize returns a callback to remove the event listener
        // https://github.homeawaycorp.com/traveler/raf-optimized/blob/8f607c662b99f6da33724b3c563e97eba86ef740/src/index.js#L163
        this.removeResizeEventListener = resize(this.adjustPositioning);

        if (this.props.closeOnOutsideClick) {
            window.document.addEventListener(
                'mouseup',
                this.handleOutsideMouseClick
            );
            window.document.addEventListener(
                'touchstart',
                this.handleOutsideMouseClick
            );
        }
    };

    detachEventListeners = () => {
        this.removeResizeEventListener();

        if (this.props.closeOnOutsideClick) {
            window.document.removeEventListener(
                'mouseup',
                this.handleOutsideMouseClick
            );
            window.document.removeEventListener(
                'touchstart',
                this.handleOutsideMouseClick
            );
        }

        if (this.removeNodeScrollListener) {
            this.removeNodeScrollListener();
            this.removeNodeScrollListener = null;
        }

        if (this.removeWindowScrollListener) {
            this.removeWindowScrollListener();
            this.removeWindowScrollListener = null;
        }
    };

    initTooltipPositioner() {
        return new TooltipPositioner(
            {
                fullwidth: this.props.fullwidth,
                parentNode: this.props.parentNode,
                centered: this.props.centered,
                tooltipType: this.props.tooltipType,
                placement: this.props.placement,
                useClientHeight: this.props.useClientHeight
            },
            this.container,
            this.tooltip
        );
    }

    adjustPositioning = () => {
        if (this.tooltip && this.state.displayTooltip) {
            this.tooltipPositioner = this.initTooltipPositioner();
            const tooltipDOM = findDOMNode(this.tooltip);

            const {
                tooltipPosition,
                arrowPosition
            } = this.tooltipPositioner.getPositions();

            // Change className without triggering rerender
            // TODO: refactor this so the render function is the only
            // method setting the className
            tooltipDOM.className = this.getTooltipClassName();

            // Tooltip changes widths after it's repositioned.
            // Force it to stay the same so positioning is correct
            tooltipDOM.style.width = `${tooltipPosition.width}px`;
            tooltipDOM.style.left = `${tooltipPosition.left}px`;
            tooltipDOM.style.top = `${tooltipPosition.top}px`;

            this.tooltipArrow.style.top = arrowPosition.top;
            this.tooltipArrow.style.left = arrowPosition.left;
        }
    };

    getTooltipClassName() {
        const tooltipType = this.props.tooltipType;
        const placement = this.tooltipPositioner
            ? this.tooltipPositioner.getSafePlacement()
            : this.props.placement;

        return classNames(
            this.props.portalClassName,
            `Tooltip Tooltip--${placement}`,
            {
                // Checks for tooltip and tooltip-lg
                'Tooltip--popover': tooltipType === 'popover',
                'Tooltip--lg':
                    tooltipType === 'tooltip--lg' || tooltipType === 'popover',
                'Tooltip--full-width': this.props.fullWidth,
                in: this.state.displayTooltip
            },
            'fade'
        );
    }

    clearTimeouts = () => {
        if (this.closeTimeoutId !== null) {
            clearTimeout(this.closeTimeoutId);
            this.closeTimeoutId = null;
        }

        if (this.delayHideTimeoutId !== null) {
            clearTimeout(this.delayHideTimeoutId);
            this.delayHideTimeoutId = null;
        }

        if (this.delayShowTimeoutId !== null) {
            clearTimeout(this.delayShowTimeoutId);
            this.delayShowTimeoutId = null;
        }
    };

    handleFocus = (e) => {
        this.props.onFocus(e);
        this.handleOpen(e);
    };

    handleOpen = (e) => {
        if (this.props.trigger === 'none') {
            return;
        }

        const eventType = e.type === 'mouseover' ? 'hover' : e.type;

        // only trigger openPortal for specified triggers
        if (this.props.trigger.indexOf(eventType) !== -1) {
            const targetContainer = this.props.parentNode
                ? this.tooltip
                : this.container;
            // close on second click
            if (
                eventType === 'click' &&
                this.state.displayTooltip &&
                targetContainer &&
                !targetContainer.contains(e.target)
            ) {
                this.handleClose(e);
                return;
            }

            // set up a persist state so only a click can close tooltip
            if (eventType === 'click') {
                this.setState({
                    persist: true
                });
            }

            this.clearTimeouts();

            this.delayShowTimeoutId = setTimeout(() => {
                this.displayTooltip();
            }, this.props.delayShow);
        }
    };

    handleClose = (e) => {
        if (this.props.trigger === 'none') {
            return;
        }

        if (!this.state.persist) {
            this.clearTimeouts();
            this.delayHideTimeoutId = setTimeout(() => {
                this.fadeAndClose();
            }, this.props.delayHide);
        } else if (e.type === 'click') {
            this.fadeAndClose();
        }
    };

    handleOutsideMouseClick = (e) => {
        const targetContainer = this.props.parentNode
            ? this.tooltip
            : this.container;
        if (
            this.state.displayTooltip &&
            this.props.closeOnOutsideClick &&
            targetContainer &&
            !targetContainer.contains(e.target)
        ) {
            this.fadeAndClose();
        }
    };

    /* wait for fade out animation to occur, then remove from DOM */
    fadeAndClose() {
        if (!this.props.fadeOut) {
            this.closeTooltip();
        }

        // The current fade out transition time
        // in Baseline is 350ms so am setting this
        // slightly earlier to prevent being able
        // to mouse over tooltip after it's faded to 0 opacity
        // but still exists in DOM, which causes a flicker.
        // https://github.expedia.biz/vrbo-web-platform/baseline/blob/master/src/less/partials/component-animations.less#L4-L11
        const baselineFadeOutDuration = 350;
        const tooltipDOM = findDOMNode(this.tooltip);

        if (tooltipDOM) {
            tooltipDOM.className = tooltipDOM.className.replace('in ', '');
        }

        this.clearTimeouts();

        this.closeTimeoutId = setTimeout(() => {
            this.closeTooltip();
        }, baselineFadeOutDuration - 10);
    }

    handleKeydown = (e) => {
        const tooltipType = this.props.tooltipType;

        if (e.keyCode === this.KEYCODES.ESCAPE && this.state.displayTooltip) {
            this.fadeAndClose();
        } else if (e.keyCode === 9 && tooltipType === 'popover') {
            // Tab key
            this.trapFocus.handleTabbing(e);
        }
    };

    displayTooltip = () => {
        this.setState({
            displayTooltip: true
        });

        this.props.onOpen();
    };

    closeTooltip = () => {
        this.setState({
            displayTooltip: false
        });

        this.props.onClose();
    };

    // Helper method to render the tooltip content. Separated from the main
    // render method since it can be rendered inside and outside of a portal.
    renderTooltipContent(content, title, tooltipType, interactiveContent) {
        const isPopover = tooltipType === 'popover';
        let role = 'status';
        const innerClassNames = classNames('Tooltip__inner', {
            'text-white': !isPopover
        });

        if (interactiveContent || isPopover) {
            role = 'dialog';
        }

        const props = {
            ref: /* istanbul ignore next */ (c) => (this.tooltip = c),
            style: {
                display: 'block'
            },
            className: this.getTooltipClassName(),
            role,
            onClick: this.props.onClick
        };

        if (this.state.displayTooltip && this.contentId !== null) {
            props.id = `tipContent-${this.contentId}`;
            props['aria-live'] = interactiveContent ? 'off' : 'polite';
        }

        return (
            <span {...props}>
                <span
                    className="Tooltip__arrow"
                    ref={
                        /* istanbul ignore next */
                        (c) => (this.tooltipArrow = c)
                    }
                />
                {isPopover && title && (
                    <span className="Tooltip__title">{title}</span>
                )}
                {this.props.shouldPersist && isPopover && (
                    <ButtonIcon
                        className="Tooltip__close"
                        label={this.props.labels.close}
                        onClick={this.closeTooltip}
                        svgMarkup={CLOSE__16}
                        size="xs"
                    />
                )}
                <span className={innerClassNames}>{content}</span>
            </span>
        );
    }

    render() {
        const {
            tooltipType,
            title,
            content,
            wrappingMethod,
            wrapperClassName,
            wrapperStyle,
            parentNode,
            interactiveContent,
            interactiveContainer
        } = this.props;

        // This helps preserve the bounds of the this.props.children
        const wrapStyle = {
            display: wrappingMethod,
            outline: 0,
            position: 'relative',
            ...wrapperStyle
        };
        const usePortal = parentNode && typeof window !== 'undefined';
        const props = {
            ref: /* istanbul ignore next */ (c) => (this.container = c),
            className: wrapperClassName,
            style: wrapStyle,
            onMouseOver: this.handleOpen,
            onMouseLeave: this.handleClose,
            onFocus: this.handleFocus,
            onBlur: this.handleClose,
            onClick: this.handleOpen,
            onKeyDown: this.handleKeydown,
            role: interactiveContainer ? 'presentation' : 'button',
            tabIndex: interactiveContainer ? null : '0'
        };

        if (this.state.displayTooltip && this.contentId !== null) {
            props['aria-controls'] = `tipContent-${this.contentId}`;
        }

        return (
            <div {...props}>
                {this.props.children}
                {this.state.displayTooltip &&
                    !usePortal &&
                    this.renderTooltipContent(
                        content,
                        title,
                        tooltipType,
                        interactiveContent
                    )}
                {this.state.displayTooltip &&
                    usePortal &&
                    ReactDOM.createPortal(
                        this.renderTooltipContent(
                            content,
                            title,
                            tooltipType,
                            interactiveContent
                        ),
                        parentNode
                    )}
            </div>
        );
    }
}

export default Tooltip;
